/**
 * Read helpers over the generated help-content bundle. The bundle is the single
 * source (mirrored from docs/help/ by scripts/sync-help-docs.mjs); everything in
 * the docs UI reads through these so lookup/search/order logic lives in one place.
 */
import {
  HELP_ARTICLES,
  HELP_AUDIENCES,
  type DocArticle,
  type DocAudience,
  type DocAudienceId,
} from "./help-content.generated";

export type { DocArticle, DocAudience, DocAudienceId };
export { HELP_ARTICLES, HELP_AUDIENCES };

export function getArticle(slug: string): DocArticle | undefined {
  return HELP_ARTICLES.find((a) => a.slug === slug);
}

/** Articles for one audience, in authored order. */
export function articlesByAudience(audience: DocAudienceId): DocArticle[] {
  return HELP_ARTICLES.filter((a) => a.audience === audience).sort(
    (a, b) => a.order - b.order,
  );
}

/** Audience groups with their articles, for the sidebar. */
export function groupedArticles(): Array<{ audience: DocAudience; articles: DocArticle[] }> {
  return HELP_AUDIENCES.map((audience) => ({
    audience,
    articles: articlesByAudience(audience.id),
  })).filter((g) => g.articles.length > 0);
}

/** Prev/next within the same audience (sidebar reading order). */
export function siblingArticles(slug: string): {
  prev: DocArticle | undefined;
  next: DocArticle | undefined;
} {
  const article = getArticle(slug);
  if (!article) return { prev: undefined, next: undefined };
  const siblings = articlesByAudience(article.audience);
  const idx = siblings.findIndex((a) => a.slug === slug);
  return {
    prev: idx > 0 ? siblings[idx - 1] : undefined,
    next: idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : undefined,
  };
}

const audienceLabel = new Map(HELP_AUDIENCES.map((a) => [a.id, a.label]));
export function audienceLabelFor(id: DocAudienceId): string {
  return audienceLabel.get(id) ?? id;
}

/**
 * Map an app route to its best-matching help article, for the per-surface "?"
 * help buttons. Articles declare the routes they cover in front matter; we match
 * the current pathname against those and pick:
 *   1. the longest (most specific) declared route, so `/coach/review` beats a
 *      `/coach` article;
 *   2. on a tie (two articles declare the SAME route — e.g. coach-review's
 *      telestrator and tactical-board both cover `/coach/review`), the lower
 *      `order` wins, so the canonical article for that surface is stable and
 *      independent of array/iteration order. The "?" opens that article; the
 *      others remain reachable via the sidebar and the article's Related links.
 */
export function articleForRoute(pathname: string): DocArticle | undefined {
  let best: { article: DocArticle; len: number } | undefined;
  for (const article of HELP_ARTICLES) {
    for (const route of article.routes) {
      // Normalize TanStack-style params ($slug) to a prefix match on the static head.
      const staticHead = route.split("/$")[0];
      if (
        staticHead &&
        (pathname === staticHead ||
          pathname.startsWith(staticHead + "/") ||
          pathname.startsWith(staticHead + "?"))
      ) {
        const longer = !best || staticHead.length > best.len;
        const tieLowerOrder =
          best && staticHead.length === best.len && article.order < best.article.order;
        if (longer || tieLowerOrder) best = { article, len: staticHead.length };
      }
    }
  }
  return best?.article;
}

/** Lightweight client-side search across title, audience, headings, and body. */
export type DocSearchHit = {
  article: DocArticle;
  score: number;
  snippet: string;
};

export function searchArticles(query: string): DocSearchHit[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  const hits: DocSearchHit[] = [];
  for (const article of HELP_ARTICLES) {
    const title = article.title.toLowerCase();
    const headings = article.headings.map((h) => h.text.toLowerCase()).join(" ");
    const body = article.body.toLowerCase();
    let score = 0;
    for (const term of terms) {
      if (title.includes(term)) score += 10;
      if (headings.includes(term)) score += 4;
      if (body.includes(term)) score += 1;
    }
    if (score === 0) continue;
    hits.push({ article, score, snippet: makeSnippet(article.body, terms[0]) });
  }
  return hits.sort((a, b) => b.score - a.score).slice(0, 12);
}

/** A short, cleaned context window around the first matched term. */
function makeSnippet(body: string, term: string): string {
  const plain = body
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "") // images
    .replace(/<!--[\s\S]*?-->/g, "") // SHOT comments
    .replace(/[#*`>_|-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const idx = plain.toLowerCase().indexOf(term);
  if (idx === -1) return plain.slice(0, 120) + (plain.length > 120 ? "…" : "");
  const start = Math.max(0, idx - 50);
  const end = Math.min(plain.length, idx + 90);
  return (start > 0 ? "…" : "") + plain.slice(start, end).trim() + (end < plain.length ? "…" : "");
}
